import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Award, Users, Target, Handshake, Star, MapPin, Phone, Mail, Globe } from "lucide-react";

const RAAD_PHOTO = "https://customer-assets.emergentagent.com/job_raad-realestate/artifacts/xek4wr2m_IMG_8542.jpeg";

const values = [
  {
    icon: Target,
    title: "Straight Talk",
    description:
      "I don't sugarcoat things. You deserve to know the truth about your home's value, the market, and your options.",
  },
  {
    icon: Handshake,
    title: "Fierce Negotiation",
    description:
      "I fight for every dollar. Whether buying or selling, I negotiate like it's my own money on the line.",
  },
  {
    icon: Users,
    title: "Personal Attention",
    description:
      "You're not a number. I handle every client personally because your transaction deserves my full attention.",
  },
  {
    icon: Award,
    title: "Proven Results",
    description:
      "150+ homes sold. A flawless record of five-star reviews. 15 years of experience. The results speak for themselves.",
  },
];

const specialties = [
  "Buyer's Agent",
  "Listing Agent",
  "Foreclosure",
  "First-Time Buyers",
  "Investment Properties",
  "Luxury Homes",
];

const serviceAreas = [
  "Los Angeles County",
  "San Bernardino County",
  "Riverside County",
  "Orange County",
  "San Diego County",
  "Ventura County",
];

export default function AboutPage() {
  return (
    <div className="page-transition" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src={RAAD_PHOTO}
                  alt="Raad Shalabi"
                  className="w-full aspect-[3/4] object-cover object-top"
                  data-testid="about-hero-image"
                />
                <div className="absolute inset-0 border border-[#D4AF37]/20" />
                <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] p-8">
                  <p className="text-black font-bold text-5xl">2011</p>
                  <p className="text-black/70 text-sm uppercase tracking-wider">
                    Since
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-px w-12 bg-[#D4AF37]" />
                  <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm">
                    Broker Associate / Real Estate Agent
                  </span>
                </div>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white">
                  Raad <span className="text-[#D4AF37]">Shalabi</span>
                </h1>
              </div>

              <div className="space-y-6 text-lg">
                <p className="text-zinc-300 leading-relaxed">
                  Aside from the substantial amount of unprofessionalism this industry 
                  contains, the second largest issue is the fact that the vast majority 
                  of sellers and buyers are immediately misguided and mistreated the 
                  moment they open escrow.
                </p>
                <p className="text-zinc-400 leading-relaxed">
                  I take great pride in delivering extraordinary service to all my clients 
                  and always ensure that I've utilized and exhausted every route possible 
                  to effectively represent my client's individual needs. With 150+ homes 
                  sold and 15 years of experience, I've built a career on doing right by people.
                </p>
              </div>

              <div className="flex items-center gap-4 pt-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={`about-star-${n}`} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <span className="text-white font-bold">5.0</span>
                <span className="text-zinc-400 text-sm">5-star reviews on Zillow</span>
              </div>

              <div className="grid grid-cols-2 gap-6 pt-4 border-t border-white/10">
                <div className="space-y-1">
                  <p className="text-[#D4AF37] font-bold text-sm uppercase tracking-wider">Brokerage</p>
                  <p className="text-white font-semibold">RE/MAX TIME</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[#D4AF37] font-bold text-sm uppercase tracking-wider">License</p>
                  <p className="text-white font-semibold">DRE #01905641</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-y border-[#D4AF37]/20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="text-[#D4AF37] text-8xl font-serif leading-none">"</div>
          <blockquote className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight -mt-8">
            I'm not here to sugarcoat.{" "}
            <span className="text-[#D4AF37]">I'm here to get you results.</span>
          </blockquote>
          <p className="text-zinc-400 mt-8 uppercase tracking-widest text-sm">— Raad Shalabi</p>
        </div>
      </section>

      {/* What Makes Me Different */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
              What Makes Me <span className="text-[#D4AF37]">Different</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              In an industry full of smooth talkers, I choose to be real.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="card-dark p-8 flex gap-6 animate-fadeInUp hover:border-[#D4AF37]/50 transition-colors"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`value-card-${value.title}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#D4AF37] flex items-center justify-center">
                    <value.icon className="w-8 h-8 text-black" />
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-white">{value.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats + Specialties */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24">
            <div className="text-center space-y-2" data-testid="stat-sales">
              <p className="text-5xl md:text-6xl font-black text-[#D4AF37]">150+</p>
              <p className="text-zinc-400 uppercase tracking-wider text-sm">Homes Sold</p>
            </div>
            <div className="text-center space-y-2" data-testid="stat-experience">
              <p className="text-5xl md:text-6xl font-black text-[#D4AF37]">15</p>
              <p className="text-zinc-400 uppercase tracking-wider text-sm">Years Experience</p>
            </div>
            <div className="text-center space-y-2" data-testid="stat-rating">
              <div className="flex items-center justify-center gap-1">
                <p className="text-5xl md:text-6xl font-black text-[#D4AF37]">5.0</p>
                <Star className="w-8 h-8 text-[#D4AF37] fill-[#D4AF37]" />
              </div>
              <p className="text-zinc-400 uppercase tracking-wider text-sm">Star Rating</p>
            </div>
            <div className="text-center space-y-2" data-testid="stat-price">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37]">$598K</p>
              <p className="text-zinc-400 uppercase tracking-wider text-sm">Avg Sale Price</p>
            </div>
          </div>

          {/* Specialties & Service Areas */}
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white">Specialties</h3>
              <div className="flex flex-wrap gap-3">
                {specialties.map((spec) => (
                  <span
                    key={spec}
                    className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 text-sm font-semibold"
                    data-testid={`specialty-${spec}`}
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white">Service Areas</h3>
              <div className="grid grid-cols-2 gap-3">
                {serviceAreas.map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-zinc-300 text-sm">{area}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Globe className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-zinc-300 text-sm">
                  Speaks <span className="text-white font-semibold">English, Spanish, Arabic & Portuguese</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Info */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-px w-12 bg-[#D4AF37]" />
                  <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm">Office Location</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
                  Let's <span className="text-[#D4AF37]">Connect</span>
                </h2>
              </div>

              <div className="space-y-6">
                <a href="tel:+19092335411" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors">
                    <Phone className="w-6 h-6 text-[#D4AF37] group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-xl">909.233.5411</p>
                    <p className="text-zinc-400 text-sm">Call or text anytime</p>
                  </div>
                </a>

                <a href="mailto:raadshalabi41@gmail.com" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors">
                    <Mail className="w-6 h-6 text-[#D4AF37] group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Raadshalabi41@gmail.com</p>
                    <p className="text-zinc-400 text-sm">I respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">RE/MAX TIME</p>
                    <p className="text-zinc-400 text-sm">10535 E Foothill Blvd #460</p>
                    <p className="text-zinc-400 text-sm">Rancho Cucamonga, CA 91730</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-dark p-8 text-center space-y-6 border-[#D4AF37]/30">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ab/RE-MAX_logo.png/320px-RE-MAX_logo.png" 
                alt="RE/MAX" 
                className="h-16 mx-auto object-contain"
              />
              <div className="space-y-2">
                <p className="text-white text-2xl font-bold">The Shalabi Real Estate Group</p>
                <p className="text-zinc-400">Powered by RE/MAX TIME</p>
              </div>
              <div className="h-px w-24 bg-[#D4AF37]/30 mx-auto" />
              <p className="text-zinc-500 text-sm">DRE #01905641</p>
              <p className="text-zinc-400 text-sm">
                Price Range: <span className="text-white font-semibold">$138K – $1.5M</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-t border-[#D4AF37]/20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Ready to <span className="text-[#D4AF37]">Get Started?</span>
          </h2>
          <p className="text-zinc-400 text-lg">
            Let's talk about your real estate goals. No pressure, just a real
            conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest px-8 py-6"
                data-testid="about-contact-button"
              >
                Contact Me
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:+19092335411">
              <Button
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-black rounded-none font-bold uppercase tracking-widest px-8 py-6"
                data-testid="about-call-button"
              >
                Call 909.233.5411
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
